import type {
  SDKConfig,
  PageTranslateConfig,
  ParagraphTranslateConfig,
  TranslatePlugin,
  PluginContext,
  ViewportObserverOptions,
  DOMChangeDetectorOptions,
  CacheOptions,
  CacheConfig,
} from './types';
import { CACHE_TYPE } from './types';
import { ConfigManager } from './core/config';
import { EventBus } from './core/event-bus';
import { PluginManager } from './core/plugin-manager';
import { I18nManager } from './core/i18n';
import { TranslateService } from './service/translate';
import { TranslateQueue } from './service/translate-queue';
import { CacheManager } from './service/cache-manager';
import { LanguageService } from './service/language';
import { ViewportObserver } from './core/viewport-observer';
import { DOMChangeDetector } from './core/dom-change-detector';
import { gateEntryConfig, isCacheEntryEnabled } from './entry-gate';

export interface TranslateSDKOptions extends SDKConfig {
  cacheManager?: CacheManager;
}

export class TranslateSDK {
  private config: ConfigManager;
  private eventBus: EventBus;
  private pluginManager: PluginManager;
  private i18n: I18nManager;
  private translateService: TranslateService;
  private translateQueue: TranslateQueue;
  private cacheManager: CacheManager;
  private languageService: LanguageService;
  private ctx: PluginContext;

  constructor(sdkConfig: TranslateSDKOptions) {
    const gated = gateEntryConfig(sdkConfig);
    this.config = new ConfigManager(gated);
    this.eventBus = new EventBus();
    this.pluginManager = new PluginManager();
    this.i18n = new I18nManager(this.config.locale);
    this.languageService = new LanguageService();

    this.translateService = new TranslateService(this.config, this.languageService);

    this.cacheManager =
      isCacheEntryEnabled() && sdkConfig.cacheManager
        ? sdkConfig.cacheManager
        : new CacheManager(this.config.cache);

    this.translateQueue = new TranslateQueue({
      chunkSize: this.config.chunkSize,
      translateService: this.translateService,
      cacheManager: this.cacheManager,
    });

    this.ctx = {
      config: this.config,
      queue: this.translateQueue,
      language: this.languageService,
      events: this.eventBus,
      i18n: this.i18n,
      createViewportObserver: (options: ViewportObserverOptions) =>
        new ViewportObserver(options),
      createDOMChangeDetector: (options: DOMChangeDetectorOptions) =>
        new DOMChangeDetector(options),
    };

    this.pluginManager.setContext(this.ctx);

    if (gated.plugins) {
      for (const plugin of gated.plugins) {
        this.pluginManager.register(plugin);
      }
    }
  }

  async pageTranslate(config: PageTranslateConfig): Promise<void> {
    await this.pluginManager.initPlugin('pure-page', config);
  }

  async paragraphTranslate(config: ParagraphTranslateConfig): Promise<void> {
    await this.pluginManager.initPlugin('paragraph-compare', config);
  }

  addPlugin(plugin: TranslatePlugin): void {
    this.pluginManager.register(plugin);
  }

  get storage() {
    return {
      set: (
        source: string | Array<{ source: string; targets?: Record<string, string> }>,
        targets?: Record<string, string>,
      ) => {
        if (!isCacheEntryEnabled()) return;
        if (typeof source === 'string') {
          this.cacheManager.set(source, targets);
        } else {
          this.cacheManager.set(source);
        }
      },
      clear: (cacheOptions?: CacheOptions) => {
        if (!isCacheEntryEnabled()) return;
        if (cacheOptions) {
          const resolved: CacheConfig = {
            enabled: true,
            type: cacheOptions.type ?? CACHE_TYPE.LOCAL_STORAGE,
            expireTime: cacheOptions.expireTime ?? -1,
          };
          new CacheManager(resolved).clear();
        } else {
          this.cacheManager.clear();
        }
      },
    };
  }

  destroy(): void {
    this.pluginManager.destroyAll();
    this.translateQueue.destroy();
    this.translateService.destroy();
    this.eventBus.removeAllListeners();
  }

  removeTranslations(): void {
    this.pluginManager.removeAll();
  }
}
